import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { canSeeRevenue } from '../../lib/perms';
import CompensationLadder from '../common/CompensationLadder';
import ApplicationAssignControl from './ApplicationAssignControl';
import {
  Mail,
  Phone,
  MapPin,
  Building2,
  FileText,
  Cake,
  Briefcase,
  CalendarCheck,
  CalendarDays,
  Pencil,
  Check,
  X,
  Loader2,
  History,
  Sparkles,
} from 'lucide-react';
import { candidatesApi, commandCenterApi, getErrorMessage } from '../../services/api';
import { toast } from 'sonner';

// iter_48: format ISO date as a human-readable string (no extra deps).
const fmtDob = (iso) => {
  if (!iso || typeof iso !== 'string') return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtINR = (rupees) => {
  if (rupees == null || rupees === '') return null;
  const n = Number(rupees);
  if (!Number.isFinite(n) || n <= 0) return null;
  // Convert raw rupees → LPA for display
  const lpa = Math.round((n / 100000) * 100) / 100;
  return `₹${lpa.toLocaleString('en-IN')} LPA`;
};

const STAGE_LABEL = {
  sourced: 'Sourced',
  screening: 'Screening',
  client_submission: 'Submitted',
  interview_scheduled: 'Interview',
  interview_selected: 'Selected',
  offer_released: 'Offered',
  joined: 'Joined',
  rejected: 'Rejected',
  dropped: 'On Hold',
  dropped_out: 'Dropped',
};

const STAGE_BADGE = {
  joined: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  offer_released: 'bg-teal-100 text-teal-700 border-teal-200',
  interview_selected: 'bg-violet-100 text-violet-700 border-violet-200',
  rejected: 'bg-rose-100 text-rose-700 border-rose-200',
  dropped: 'bg-amber-100 text-amber-700 border-amber-200',
  dropped_out: 'bg-rose-100 text-rose-700 border-rose-200',
};

const CandidateViewDialog = ({ isOpen, onClose, candidate, onViewResume, user, onCandidateUpdated }) => {
  const showRevenue = canSeeRevenue(user);
  const [apps, setApps] = React.useState([]);
  const [appsLoading, setAppsLoading] = React.useState(false);
  // Inline-edit state for the manual Offer Letter Date field.
  // editingId = the application_id currently being edited (or null).
  // draft     = the in-progress YYYY-MM-DD string.
  // savingId  = appId currently flushing to the API (disables UI).
  const [editingId, setEditingId] = React.useState(null);
  const [draft, setDraft] = React.useState('');
  const [savingId, setSavingId] = React.useState(null);
  // OCR re-parse state
  const [reparsing, setReparsing] = React.useState(false);

  // Detect if the extracted resume text looks empty or too short — the recruiter
  // then benefits most from a one-click OCR rescue. Also show the button whenever
  // a resume file exists (so the user can force a re-parse even if the text looks
  // fine — sometimes AI parse missed skills / designation on a good extraction).
  const hasResumeFile = Boolean(candidate?.resume_url || candidate?.gridfs_resume_id);
  const resumeText = candidate?.resume_text || '';
  const resumeLooksEmpty = hasResumeFile && resumeText.trim().length < 80;

  const handleReparse = async () => {
    if (!candidate?.id || reparsing) return;
    setReparsing(true);
    try {
      const { data } = await candidatesApi.reparseResume(candidate.id, true);
      const chars = data?.extracted_chars || 0;
      const fields = Array.isArray(data?.fields_updated) ? data.fields_updated : [];
      if (data?.ocr_used) {
        toast.success(
          fields.length
            ? `OCR extracted ${chars} chars — filled ${fields.length} blank field${fields.length > 1 ? 's' : ''}: ${fields.join(', ')}`
            : `OCR extracted ${chars} chars — profile fields were already filled, no changes made`
        );
      } else {
        toast.success(`Re-parse complete — ${chars} chars extracted (${fields.length ? `updated ${fields.length} field${fields.length > 1 ? 's' : ''}` : 'no new fields to fill'})`);
      }
      if (typeof onCandidateUpdated === 'function' && data?.candidate) {
        onCandidateUpdated(data.candidate);
      }
    } catch (e) {
      toast.error(getErrorMessage(e) || 'Re-parse failed');
    } finally {
      setReparsing(false);
    }
  };

  const startEditOfferDate = (a) => {
    setEditingId(a.id);
    // Prefill with the existing manual date or the derived offer_released_at,
    // converted to YYYY-MM-DD for <input type="date">.
    const seed = a.offer_letter_date || (a.offer_released_at ? a.offer_released_at.slice(0, 10) : '');
    setDraft(seed);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft('');
  };

  const saveOfferDate = async (appId) => {
    setSavingId(appId);
    try {
      const value = (draft || '').trim() || null; // empty string → clear
      await commandCenterApi.setApplicationOfferLetterDate(appId, value);
      // Optimistic local update so UI reflects the change instantly.
      setApps((prev) =>
        prev.map((a) => (a.id === appId ? { ...a, offer_letter_date: value } : a))
      );
      toast.success(value ? 'Offer letter date saved' : 'Offer letter date cleared');
      cancelEdit();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not save offer letter date'));
    } finally {
      setSavingId(null);
    }
  };

  React.useEffect(() => {
    if (!isOpen || !candidate?.id) {
      setApps([]);
      return;
    }
    let cancelled = false;
    setAppsLoading(true);
    candidatesApi
      .applicationsSummary(candidate.id)
      .then((res) => {
        if (!cancelled) setApps(res.data?.applications || []);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(getErrorMessage(err, 'Could not load application history'));
          setApps([]);
        }
      })
      .finally(() => {
        if (!cancelled) setAppsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, candidate?.id]);

  if (!candidate) return null;

  // Compliance toggle: recruiters see Age only; admins/managers see DOB + Age.
  // Anti-bias guard for hiring decisions per the user's iter_48 spec.
  const role = user?.role;
  const canSeeFullDob = role === 'admin' || role === 'team_lead' || role === 'delivery_manager';
  const dobIso = candidate?.date_of_birth || null;
  const ageDisplay = candidate?.age ?? (() => {
    if (!dobIso) return null;
    const d = new Date(dobIso);
    if (Number.isNaN(d.getTime())) return null;
    const today = new Date();
    let a = today.getFullYear() - d.getFullYear();
    const m = today.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) a--;
    return a;
  })();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{candidate?.name || 'Candidate Details'}</DialogTitle>
          <DialogDescription>
            {candidate?.current_designation || 'N/A'} at {candidate?.current_company || 'N/A'}
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="flex-1 max-h-[60vh] pr-4">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2 text-slate-600">
                <Mail className="w-4 h-4" />
                {candidate?.email || 'N/A'}
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <Phone className="w-4 h-4" />
                {candidate?.phone || 'N/A'}
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <MapPin className="w-4 h-4" />
                {candidate?.current_location || 'N/A'}
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <Building2 className="w-4 h-4" />
                {candidate?.experience_years ?? 0} years experience
              </div>
              {/* iter_48: DOB + Age (DOB hidden from recruiters per compliance) */}
              {(dobIso || ageDisplay !== null) && (
                <div
                  className="flex items-center gap-2 text-slate-600"
                  data-testid="candidate-view-age"
                >
                  <Cake className="w-4 h-4" />
                  {ageDisplay !== null ? `${ageDisplay} years` : ''}
                  {dobIso && canSeeFullDob ? (
                    <span className="text-slate-400">· {fmtDob(dobIso)}</span>
                  ) : null}
                </div>
              )}
            </div>

            {/* CTC Info — admin-only; non-admin roles never see ₹ */}
            {((showRevenue && (candidate?.current_ctc || candidate?.expected_ctc)) || candidate?.notice_period) && (
              <div className="bg-slate-50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Compensation & Availability</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  {showRevenue && candidate?.current_ctc && (
                    <div>
                      <p className="text-slate-500">Current CTC</p>
                      <p className="font-medium">₹{candidate.current_ctc} LPA</p>
                    </div>
                  )}
                  {showRevenue && candidate?.expected_ctc && (
                    <div>
                      <p className="text-slate-500">Expected CTC</p>
                      <p className="font-medium">₹{candidate.expected_ctc} LPA</p>
                    </div>
                  )}
                  {candidate?.notice_period && (
                    <div>
                      <p className="text-slate-500">Notice Period</p>
                      <p className="font-medium">{candidate.notice_period} days</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Compensation ladder — extracted to common/CompensationLadder
                so the Command Center detail panel can render the same view
                without copy/pasting the maths. */}
            {showRevenue && (
              <CompensationLadder
                currentL={Number(candidate?.current_ctc) || 0}
                expectedL={Number(candidate?.expected_ctc) || 0}
                apps={apps}
              />
            )}

            {candidate?.skills?.length > 0 && (
              <div>
                <h4 className="font-medium text-slate-900 mb-2">Skills</h4>
                <div className="flex flex-wrap gap-2">
                  {candidate.skills.map((skill, i) => (
                    <Badge key={i} variant="outline">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {candidate?.education && (
              <div>
                <h4 className="font-medium text-slate-900 mb-1">Education</h4>
                <p className="text-slate-600">{candidate.education}</p>
              </div>
            )}

            {candidate?.notes && (
              <div>
                <h4 className="font-medium text-slate-900 mb-1">Notes</h4>
                <p className="text-slate-600">{candidate.notes}</p>
              </div>
            )}

            {/* Application History — surfaces offer_released_at + joining_date per app */}
            <div data-testid="cand-app-history">
              <h4 className="font-medium text-slate-900 mb-2 flex items-center gap-1.5">
                <Briefcase className="w-4 h-4 text-slate-500" />
                Application History
                {apps.length > 0 ? (
                  <span className="text-xs font-normal text-slate-400">· {apps.length}</span>
                ) : null}
              </h4>
              {appsLoading ? (
                <div className="text-xs text-slate-400 italic">Loading…</div>
              ) : apps.length === 0 ? (
                <div className="text-xs text-slate-400 italic">No applications yet.</div>
              ) : (
                <div className="space-y-2">
                  {apps.map((a) => (
                    <div
                      key={a.id}
                      className="rounded-md border border-slate-200 bg-white p-3"
                      data-testid={`cand-app-row-${a.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-800 truncate">
                            {a.job_title || 'Untitled job'}
                          </div>
                          <div className="text-[11px] text-slate-500 truncate">
                            {a.client_name || '—'}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <ApplicationAssignControl
                            applicationId={a.id}
                            assignedTo={a.assigned_to || null}
                            assigneeName={a.assignee_name || null}
                            onChange={(newId, newName) => {
                              setApps((prev) => prev.map((row) => (
                                row.id === a.id
                                  ? { ...row, assigned_to: newId, assignee_name: newName }
                                  : row
                              )));
                            }}
                          />
                          <Badge
                            variant="outline"
                            className={`text-[10px] ${STAGE_BADGE[a.stage] || ''}`}
                          >
                            {STAGE_LABEL[a.stage] || a.stage}
                          </Badge>
                        </div>
                      </div>
                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-1 text-[11px]">
                        <div className="flex items-center gap-1.5 text-slate-600 min-h-[24px]" data-testid={`cand-app-offer-date-${a.id}`}>
                          <CalendarCheck className="w-3 h-3 text-teal-600 flex-shrink-0" />
                          <span className="text-slate-400">Offer Letter Date:</span>
                          {editingId === a.id ? (
                            <span className="flex items-center gap-1 ml-0.5">
                              <input
                                type="date"
                                value={draft}
                                max={new Date().toISOString().slice(0, 10)}
                                onChange={(e) => setDraft(e.target.value)}
                                disabled={savingId === a.id}
                                className="h-6 px-1.5 text-[11px] border border-slate-300 rounded bg-white focus:ring-1 focus:ring-teal-500 outline-none"
                                data-testid={`cand-app-offer-date-input-${a.id}`}
                                autoFocus
                              />
                              <button
                                type="button"
                                onClick={() => saveOfferDate(a.id)}
                                disabled={savingId === a.id}
                                className="text-emerald-600 hover:text-emerald-700 disabled:opacity-50"
                                data-testid={`cand-app-offer-date-save-${a.id}`}
                                title="Save"
                              >
                                {savingId === a.id ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <Check className="w-3.5 h-3.5" />
                                )}
                              </button>
                              <button
                                type="button"
                                onClick={cancelEdit}
                                disabled={savingId === a.id}
                                className="text-slate-400 hover:text-slate-600 disabled:opacity-50"
                                data-testid={`cand-app-offer-date-cancel-${a.id}`}
                                title="Cancel"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </span>
                          ) : (
                            <span className="flex items-center gap-1.5">
                              <span className="font-medium text-slate-700">
                                {fmtDob(a.offer_letter_date) || fmtDob(a.offer_released_at) || '—'}
                              </span>
                              {a.offer_letter_date ? (
                                <span
                                  className="text-[9px] text-teal-600 bg-teal-50 border border-teal-200 rounded px-1 py-0 leading-none"
                                  title="Manually entered offer letter date (overrides stage-change timestamp)"
                                >
                                  manual
                                </span>
                              ) : null}
                              <button
                                type="button"
                                onClick={() => startEditOfferDate(a)}
                                className="text-slate-400 hover:text-teal-600 transition-colors"
                                data-testid={`cand-app-offer-date-edit-${a.id}`}
                                title="Edit offer letter date"
                              >
                                <Pencil className="w-3 h-3" />
                              </button>
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 text-slate-600" data-testid={`cand-app-joining-date-${a.id}`}>
                          <CalendarDays className="w-3 h-3 text-emerald-600" />
                          <span className="text-slate-400">Joining Date:</span>
                          <span className="font-medium text-slate-700">
                            {fmtDob(a.joining_date) || '—'}
                          </span>
                        </div>
                        <div className="text-slate-600">
                          <span className="text-slate-400">Offered CTC:</span>{' '}
                          <span className="font-medium text-slate-700">
                            {fmtINR(a.offered_ctc) || '—'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* View Resume + Re-parse OCR Buttons */}
            {hasResumeFile && (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => onViewResume(candidate)}
                  data-testid="view-resume-detail-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  View Resume
                </Button>
                <Button
                  variant={resumeLooksEmpty ? 'default' : 'outline'}
                  onClick={handleReparse}
                  disabled={reparsing}
                  className={resumeLooksEmpty ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}
                  data-testid="reparse-resume-btn"
                  title={
                    resumeLooksEmpty
                      ? 'This resume has little or no extracted text — click to OCR-rescue it'
                      : 'Force an OCR re-scan of this resume and refill any missing profile fields'
                  }
                >
                  {reparsing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Re-parsing…
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      {resumeLooksEmpty ? 'Re-parse with OCR' : 'Re-parse resume'}
                    </>
                  )}
                </Button>
                {resumeLooksEmpty && (
                  <span
                    className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5"
                    data-testid="resume-empty-hint"
                  >
                    Little or no text extracted — try OCR
                  </span>
                )}
              </div>
            )}

            {/* Last-edit lineage — surfaces who last touched the profile and
                when, so any recruiter can quickly verify freshness without
                digging through the activity log. Falls back to the upload
                lineage when the profile has never been edited post-upload. */}
            <div
              className="border-t border-slate-100 pt-3 mt-2 flex items-start gap-2 text-xs text-slate-500"
              data-testid="candidate-view-last-edited"
            >
              <History className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
              <div>
                {candidate?.updated_at ? (
                  <span>
                    <span className="text-slate-400">Last edited</span>{' '}
                    <span className="font-medium text-slate-700">
                      {fmtDob(candidate.updated_at) || new Date(candidate.updated_at).toLocaleDateString('en-IN')}
                    </span>
                    {candidate?.updated_by_name ? (
                      <>
                        {' '}
                        <span className="text-slate-400">by</span>{' '}
                        <span className="font-medium text-slate-700">{candidate.updated_by_name}</span>
                      </>
                    ) : null}
                  </span>
                ) : (
                  <span className="italic text-slate-400">
                    Not edited since upload
                  </span>
                )}
                {candidate?.created_at ? (
                  <span className="block text-slate-400 mt-0.5">
                    Uploaded {fmtDob(candidate.created_at) || ''}
                    {candidate?.created_by_name ? <> by <span className="text-slate-600">{candidate.created_by_name}</span></> : null}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default CandidateViewDialog;
